/**
 * Quick task 261002-dfh — the human decision on a Factory™ Triager proposal.
 * Internal-only; both routes are admin / task-project-PM gated server side.
 */
import { apiGet, apiPost } from './client';
import type { RobotHeldBy } from '../../utils/robotHold';

export type TriagerProposalDecision = 'declined' | 'accepted';

/**
 * MKS-278 — the state behind the decision, so the section can tell an OPEN proposal apart from
 * one a human already approved while an MKS-241 overlap hold was on. Both are unassigned and
 * still labelled `robot-candidate`.
 */
export interface TriagerProposalState {
  taskId: number;
  code: string;
  /** Non-null exactly while the task is approved and waiting for a hold to clear. */
  robotHoldApprovedAt: string | null;
  approvedBy: { id: number; name: string | null } | null;
  /** What the task waits for right now; empty once the hold is clear. */
  heldBy: RobotHeldBy[];
}

/** GET /api/tasks/:taskId/triager-proposal — read-only, same gate as the two decisions below. */
export async function getTriagerProposalState(taskId: number): Promise<TriagerProposalState> {
  const res = await apiGet<{ data: TriagerProposalState }>(`/tasks/${taskId}/triager-proposal`);
  return res.data;
}

export interface TriagerProposalResult {
  taskId: number;
  code: string;
  decision: TriagerProposalDecision;
}

/**
 * POST /api/tasks/:taskId/triager-proposal/decline — removes `robot-candidate`,
 * adds `no-robot` (the triager never picks the task), audited.
 */
export async function declineTriagerProposal(taskId: number): Promise<TriagerProposalResult> {
  // `{}` (not undefined): apiFetch sends the JSON Content-Type on every call,
  // and Fastify rejects an empty body under that header.
  const res = await apiPost<{ data: TriagerProposalResult }>(
    `/tasks/${taskId}/triager-proposal/decline`,
    {},
  );
  return res.data;
}

/** POST /api/tasks/:taskId/triager-proposal/accept — assigns the Robot. */
export async function acceptTriagerProposal(taskId: number): Promise<TriagerProposalResult> {
  const res = await apiPost<{ data: TriagerProposalResult }>(
    `/tasks/${taskId}/triager-proposal/accept`,
    {},
  );
  return res.data;
}
